import { ProductVersionColumns } from '@/enums/global/ProductVersionColumns';
import i18n from '@/i18n';

// Interface for column definition used in table exports
export interface ExportColumn {
    title: string;
    dataIndex: string;
}

interface ExportRow {
    [key: string]: any;
    children?: ExportRow[];
}

/**
 * Flatten expandable table rows in the same order in which they are displayed:
 * parent row first, followed by all of its child rows.
 */
function flattenTableRows(rows: ExportRow[]): ExportRow[] {
    const flattenedRows: ExportRow[] = [];

    rows.forEach((row) => {
        flattenedRows.push(row);

        if (Array.isArray(row.children)) {
            flattenedRows.push(...flattenTableRows(row.children));
        }
    });

    return flattenedRows;
}

// Escape CSV values that contain special characters
export function escapeCsvValue(value: string): string {
    // Ensure values with commas, semicolons, quotes, or newlines are properly escaped
    if (value.includes(',') || value.includes(';') || value.includes('"') || value.includes('\n')) {
        // Escape double quotes by doubling them and wrap in quotes
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

// Generate CSV content from the data
function generateCSV(data: any[], ignoreColumns: string[] = [], isProductVersion: boolean) {
    if (!data || data.length === 0) {
        return '';
    }

    // Step 1: Determine the headers
    const headers = Object.keys(data[0]).filter((key) => !ignoreColumns.includes(key));

    // Step 2: Create header row with proper column names
    let csvContent = '';
    if (isProductVersion) {
        // Use translated column names for headers
        const headerRow = headers
            .map((key) => {
                const headerKey = ProductVersionColumns[key as keyof typeof ProductVersionColumns] || key;
                const headerValue = i18n.t(headerKey) as string;
                return escapeCsvValue(headerValue);
            })
            .join(';');
        csvContent += headerRow + '\r\n';
    }

    // Step 3: Add data rows
    data.forEach((item) => {
        const row = headers
            .map((key) => {
                // Ensure values with commas are properly quoted
                const value = item[key] === null || item[key] === undefined ? '' : String(item[key]);
                return escapeCsvValue(value);
            })
            .join(';');

        csvContent += row + '\r\n';
    });

    return csvContent;
}

// Create download link for CSV file
export function downloadCSV(csvContent: string, fileName: string) {
    // Add BOM for Excel to properly detect UTF-8 encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up to avoid memory leaks
}

// Generate CSV and trigger download
export function downloadExcelFile(
    data: any[],
    fileName: string = 'products',
    ignoreColumns: string[] = [],
    isProductVersion = true
) {
    if (!data || data.length === 0) {
        console.error('No data provided for CSV export');
        return;
    }

    const csvContent = generateCSV(data, ignoreColumns, isProductVersion);
    downloadCSV(csvContent, fileName);
}

/**
 * Export table data to Excel/CSV file
 * @param columns - Array of column definitions with title and dataIndex
 * @param data - Array of row data objects
 * @param fileName - Name of the file (without extension, date will be appended)
 */
export function downloadTableAsExcel(columns: ExportColumn[], data: any[], fileName: string) {
    if (!data || data.length === 0) {
        console.error('No data provided for CSV export');
        return;
    }

    const csvRows: string[] = [];

    // 1. Create header row from column titles
    const headerRow = columns
        .map((col) => {
            const title = col.title.toString();
            return escapeCsvValue(title);
        })
        .join(';');

    csvRows.push(headerRow);

    // 2. Create data rows, including rows nested under expandable table groups
    flattenTableRows(data).forEach((row) => {
        const rowCells = columns.map((column) => {
            const value = row[column.dataIndex] ?? '';
            const strValue = String(value);
            return escapeCsvValue(strValue);
        });

        csvRows.push(rowCells.join(';'));
    });

    // 3. Join all rows with newlines
    const csvContent = csvRows.join('\r\n');

    // 4. Generate filename with date and download
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
        today.getDate()
    ).padStart(2, '0')}`;
    const fullFileName = `${fileName}-${dateStr}`;

    downloadCSV(csvContent, fullFileName);
}
